import { SvgIcon, theme } from '@salecycle/ui-component-library';
import { TooltipWithTitle } from 'components/experimental/TooltipWithTitle';
import { ReactNode } from 'react';
import styled from 'styled-components';
import { WorkflowActionsMenu } from './WorkflowActionsMenu';
import { DrawerType } from '../../state/Drawer/DrawerState';
import { Variant } from '../../state/SplitTests/SplitTestsState';
import { WorkflowBadge } from '../Canvas/components/WorkflowBadge';
import { DefaultVariant } from '../Workflows/components/DefaultVariant';
import { useTranslation } from 'react-i18next';

interface Props {
  id?: string;
  icon?: ReactNode;
  title: string;
  name?: string;
  actionsMenuAriaLabel?: string;
  onRemoveAriaLable?: string;
  nameAriaLabel?: string;
  tooltipProps?: {
    title: string;
    content: string;
  };
  variants?: Variant[];
  titleWithPercent?: boolean;
  variantId?: string;
  onEditVariant?: () => void;
  isActionMenuButtonOnly?: boolean;
  onRemove?: () => void;
  onOpenConfiguration: (id?: string, type?: DrawerType) => void;
  onEnableSplitTest?: () => void;
  removeMargin?: boolean;
}

const Header = styled.div<{ removeMargin: boolean }>`
  ${({ removeMargin }) => (removeMargin ? '' : 'margin-bottom: 16px;')}
  display: flex;
  justify-content: space-between;
`;

const Title = styled.div`
  font: ${({ theme }) => theme.designToken.headingH3};
  display: flex;
  justify-content: center;
  align-items: center;
  background: transparent;
`;

const IconArea = styled.div`
  margin-right: ${({ theme }) => theme.designToken.spacing2};
  color: ${({ theme }) => theme.vars.newColor.darkGS2};
`;

const TooltipArea = styled.div`
  position: relative;
  color: ${({ theme }) => theme.vars.newColor.darkGS2};
`;

const InfoArea = styled.div`
  display: flex;
`;

const TooltipIcon = styled.div`
  margin-left: 4px;
`;

const Name = styled.div`
  font: ${({ theme }) => theme.designToken.body2};
  overflow: hidden;
  color: ${({ theme }) => theme.designToken.neutral700};
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 224px;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
`;

const NameArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const IDContainer = styled.div`
  font: ${({ theme }) => theme.designToken.body2};
  overflow: hidden;
  color: ${({ theme }) => theme.designToken.neutral700};
`;

export function WorkflowBoxHeader({
  id,
  icon,
  title,
  name,
  actionsMenuAriaLabel,
  onRemoveAriaLable,
  nameAriaLabel,
  tooltipProps,
  isActionMenuButtonOnly,
  variants,
  titleWithPercent,
  variantId,
  onEditVariant,
  onRemove,
  onOpenConfiguration,
  onEnableSplitTest,
  removeMargin,
}: Props) {
  const ButtonOnlyActionsMenu = (
    <WorkflowActionsMenu actionsMenuAriaLabel={actionsMenuAriaLabel} buttonOnly onClick={onOpenConfiguration} />
  );
  const ActionsMenu = (
    <WorkflowActionsMenu
      actionsMenuAriaLabel={actionsMenuAriaLabel}
      onRemoveAriaLable={onRemoveAriaLable}
      onDelete={onRemove}
      onEdit={onOpenConfiguration}
      onEnableSplitTest={onEnableSplitTest}
      variants={variants}
      onEditVariant={onEditVariant}
    />
  );
  const { t } = useTranslation('campaigns');

  return (
    <Header removeMargin={removeMargin}>
      <InfoArea>
        {icon && <IconArea>{icon}</IconArea>}
        <NameArea>
          <TitleContainer>
            {titleWithPercent ? (
              <WorkflowBadge variantId={variantId}>{title}</WorkflowBadge>
            ) : (
              <Title style={{ flexGrow: 1 }}>{title}</Title>
            )}
            <DefaultVariant variantId={variantId} />
            {tooltipProps && (
              <TooltipArea>
                <TooltipWithTitle title={tooltipProps.title} content={tooltipProps.content}>
                  <TooltipIcon>
                    <SvgIcon name="circle-question" size="24" color={theme.designToken.primary700} />
                  </TooltipIcon>
                </TooltipWithTitle>
              </TooltipArea>
            )}
          </TitleContainer>
          {name && <Name aria-label={nameAriaLabel}>{name}</Name>}
          {id && (
            <IDContainer aria-label={t('onsite.editor.workflow.splitTest.workFlowBoxIDAria')}>
              {t('onsite.editor.workflow.splitTest.workFlowBoxID')}:{id}
            </IDContainer>
          )}
        </NameArea>
      </InfoArea>
      {isActionMenuButtonOnly ? ButtonOnlyActionsMenu : ActionsMenu}
    </Header>
  );
}
